import Vue from 'vue';
import drivingPermitService from '@/api/driving-permit-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';
import trainingResultService from '@/api/training-result-service';
import drivingPracticeExamService from '@/api/driving-practice-exam-service';

export const getDefaultDrivingPermitFormItem = () => ({
  type: 'driving_permit',
  status: '',
  documents: [],
  new_documents: [],
  questionnaire: [],
  has_requested_ability_to_manoeuvre: false,
  have_agreed_with_toc: false,
  need_main_training: false,
});

export const getDefaultDrivingPermitFilterParams = () => ({});

const state = {
  drivingPermits: [],
  drivingPermitPagination: {},
  editedDrivingPermit: {},
  newDrivingPermit: getDefaultDrivingPermitFormItem(),
  drivingPermitValidationErrors: {},
  drivingPermitFilterParams: getDefaultDrivingPermitFilterParams(),
  permitsWithPassedExamsCount: 0,
  drivingTheoryResults: [],
  needsToRegisterForPracticeExam: false,
  drivingPermitReport: [],
  drivingPermitReportFilterParams: {},
};

const mutations = {
  SET_DRIVING_PERMITS(state, { data, meta }) {
    state.drivingPermits = data;
    state.drivingPermitPagination = {
      current_page: meta.current_page,
      per_page: meta.per_page,
      total: meta.total,
    };
  },

  SET_DRIVING_PERMIT_FILTER_PARAMS(state, params) {
    state.drivingPermitFilterParams = params;
  },

  SET_EDITED_DRIVING_PERMIT(state, drivingPermit) {
    state.drivingPermitValidationErrors = {};
    state.editedDrivingPermit = JSON.parse(JSON.stringify({ ...drivingPermit, new_documents: [] }));
  },

  STORE_DRIVING_PERMIT(state, drivingPermit) {
    state.drivingPermits.unshift(drivingPermit);
    state.drivingPermitPagination.total += 1;
    state.drivingPermitValidationErrors = {};
    state.newDrivingPermit = getDefaultDrivingPermitFormItem();
  },

  UPDATE_DRIVING_PERMIT(state, drivingPermit) {
    state.drivingPermits = updateArrayItem(state.drivingPermits, drivingPermit);
  },

  DELETE_DRIVING_PERMIT(state, drivingPermit) {
    state.drivingPermits = removeArrayItem(state.drivingPermits, drivingPermit);
    state.drivingPermitPagination.total -= 1;
  },

  SET_DRIVING_PERMIT_VALIDATION_ERRORS(state, drivingPermitValidationErrors) {
    state.drivingPermitValidationErrors = drivingPermitValidationErrors;
  },

  CLEAR_DRIVING_PERMIT_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.drivingPermitValidationErrors, field);
  },

  SET_PERMITS_WITH_PASSED_EXAMS_COUNT(state, { count }) {
    state.permitsWithPassedExamsCount = count;
  },

  SET_DRIVING_THEORY_RESULTS(state, results) {
    state.drivingTheoryResults = results;
  },

  SET_DRIVING_PRACTICE_PARTICIPATION_STATUS(state, needsToRegister) {
    state.needsToRegisterForPracticeExam = needsToRegister;
  },

  CHANGE_PERMIT_ISSUED_STATUS(state, drivingPermit) {
    state.drivingPermits = updateArrayItem(state.drivingPermits, drivingPermit);
  },

  SET_DRIVING_PERMIT_REPORT_FILTER_PARAMS(state, params) {
    state.drivingPermitReportFilterParams = params;
  },

  SET_DRIVING_PERMIT_REPORT(state, report) {
    state.drivingPermitReport = report;
  },
};

const actions = {
  fetchDrivingPermits({ commit }, params) {
    commit('SET_DRIVING_PERMIT_FILTER_PARAMS', params);
    return drivingPermitService.getPage(params).then((res) => {
      commit('SET_DRIVING_PERMITS', res.data);
      return res.data;
    });
  },

  storeDrivingPermit({ commit }, drivingPermit) {
    return drivingPermitService
      .create(drivingPermit)
      .then((res) => {
        commit('STORE_DRIVING_PERMIT', res.data.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('driving_permit_created'));
        return res.data.data;
      })
      .catch((err) => {
        commit('SET_DRIVING_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editDrivingPermit({ state, commit }, drivingPermitId) {
    const drivingPermit = state.drivingPermits?.find((c) => c.id === drivingPermitId);
    if (drivingPermit) {
      commit('SET_EDITED_DRIVING_PERMIT', drivingPermit);
      return Promise.resolve(drivingPermit);
    }
    return drivingPermitService.getById(drivingPermitId).then((res) => {
      commit('SET_EDITED_DRIVING_PERMIT', res.data.data);
      return res.data.data;
    });
  },

  updateDrivingPermit({ commit }, drivingPermit) {
    return drivingPermitService
      .update(drivingPermit)
      .then((res) => {
        commit('UPDATE_DRIVING_PERMIT', res.data.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('driving_permit_updated'));
        return res.data.data;
      })
      .catch((err) => {
        commit('SET_DRIVING_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteDrivingPermit({ commit }, drivingPermit) {
    openConfirmDialog({
      title: i18n.t('confirm_driving_permit_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      drivingPermitService.delete(drivingPermit).then(() => {
        commit('DELETE_DRIVING_PERMIT', drivingPermit);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('driving_permit_deleted'));
      });
    });
  },

  getPermitsWithPassedExamsCount({ commit }) {
    return drivingPermitService.getPassedExamCount().then((res) => {
      commit('SET_PERMITS_WITH_PASSED_EXAMS_COUNT', res.data);
      return res.data;
    });
  },

  getDrivingTheoryResults({ commit }, payload) {
    return trainingResultService.getDrivingTrainingResults(payload).then((res) => {
      commit('SET_DRIVING_THEORY_RESULTS', res.data);
      return res.data;
    });
  },

  getDrivingPracticeParticipationStatus({ commit }, payload) {
    return drivingPracticeExamService.getParticipationStatus(payload).then((res) => {
      commit(
        'SET_DRIVING_PRACTICE_PARTICIPATION_STATUS',
        res.data.does_user_need_to_register_for_exam,
      );
      return res.data;
    });
  },

  changePermitIssuedStatus({ commit }, payload) {
    return drivingPermitService.changeIssuedStatus(payload).then((res) => {
      commit('CHANGE_PERMIT_ISSUED_STATUS', res.data.data);
      eventBus.$emit(OPEN_SNACKBAR, i18n.t('issued_driving_permit_status_updated'));
      return res.data.data;
    });
  },

  fetchDrivingPermitReport({ commit }, params = {}) {
    commit('SET_DRIVING_PERMIT_REPORT_FILTER_PARAMS', params);
    return drivingPermitService.getReport(params).then((res) => {
      commit('SET_DRIVING_PERMIT_REPORT', res.data);
      return res.data;
    });
  },

  updateDrivingPermitEndAt({ commit }, payload) {
    return drivingPermitService
      .updateEndAt(payload)
      .then((res) => {
        commit('UPDATE_DRIVING_PERMIT', res.data.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_end_at_updated'));
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
